import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { IconChevron } from '../../assets/icons/IconChevron';
import { formatHistoryDate, formatCurrency } from '../../utils/formatters';
import { sortInvoiceNumbers } from '../../utils/relance/invoiceNumbers';
import { HistoryExpandedDetails } from './HistoryExpandedDetails';
import type { HistoryEventItemDto } from '../../services/eventHistoryService';

interface HistoryRowProps {
    event: HistoryEventItemDto;
    locale: string;
    isExpanded: boolean;
    onToggle: () => void;
    onReactivated?: () => void;
}

export const HistoryRow: React.FC<HistoryRowProps> = ({ event, locale, isExpanded, onToggle, onReactivated }) => {
    const { t } = useTranslation();

    const isRelance = event.type === 'RELANCE_COMPLETED';
    const isDeactivated = event.type === 'RELANCE_DEACTIVATED';
    const isReactivated = event.type === 'RELANCE_REACTIVATED';
    const isPaid = event.type === 'RELANCE_PAID';
    const isPaymentsApplied = event.type === 'TRANSPHERE_PAYMENTS_APPLIED';

    const label = isRelance
        ? t(TranslationKey.History.Types.RELANCE_COMPLETED)
        : isDeactivated
        ? t(event.snoozeUntil ? TranslationKey.History.Types.RELANCE_SNOOZED : TranslationKey.History.Types.RELANCE_DEACTIVATED)
        : isReactivated
        ? t(TranslationKey.History.Types.RELANCE_REACTIVATED)
        : isPaid
        ? t(event.isFullPayment ? TranslationKey.History.Types.RELANCE_PAID_FULL : TranslationKey.History.Types.RELANCE_PAID_PARTIAL)
        : isPaymentsApplied
        ? t(TranslationKey.History.Types.TRANSPHERE_PAYMENTS_APPLIED)
        : t(TranslationKey.History.Types.NOTE_ADDED);

    const description = isPaymentsApplied
        ? `${t(TranslationKey.History.PaymentsApplied.Invoices, { count: event.invoicesApplied })} · ${t(TranslationKey.History.PaymentsApplied.Customers, { count: event.customersUpdated })}`
        : isRelance && event.invoiceNumbers.length > 0
        ? `${event.customerName ?? ''} · ${t(TranslationKey.History.Detail.InvoiceNumber)} ${sortInvoiceNumbers(event.invoiceNumbers).join(', ')}`
        : (event.customerName ?? '');

    const amount = isPaid
        ? event.isFullPayment
            ? formatCurrency(event.amountReceived, locale)
            : `${formatCurrency(event.amountReceived, locale)} / ${formatCurrency((event.amountReceived ?? 0) + (event.remainingBalance ?? 0), locale)}`
        : isPaymentsApplied
        ? ''
        : formatCurrency(event.amountDue, locale);

    return (
        <div className="col-span-full grid grid-cols-subgrid border-t border-line-subtle" data-testid={`history-row-${event.id}`}>
            {/* Below md the detail drops under the type and the other cells span both lines; from md every
                cell auto-fills its own column in source order. */}
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isExpanded}
                className="col-span-full grid grid-cols-subgrid items-start py-m px-m md:px-l text-left text-s font-normal text-content bg-surface-elevated transition-colors hover:bg-overlay-weak cursor-pointer"
                data-testid={`history-row-toggle-${event.id}`}
            >
                <span className="row-span-2 md:row-auto whitespace-nowrap tabular-nums">
                    {formatHistoryDate(event.occurredAt, locale)}
                </span>

                <span className="col-start-2 row-start-1 md:col-auto md:row-auto min-w-0 truncate">
                    {label}
                </span>

                <span className="col-start-2 row-start-2 md:col-auto md:row-auto min-w-0 truncate">
                    {description}
                </span>

                <span className="row-span-2 md:row-auto whitespace-nowrap tabular-nums text-right">
                    {amount}
                </span>

                <span className="row-span-2 md:row-auto flex h-lh items-center">
                    <IconChevron
                        className={`text-primary-700 transition-transform duration-150 ${isExpanded ? '-rotate-90' : 'rotate-90'}`}
                        data-testid={`history-row-chevron-${event.id}`}
                    />
                </span>
            </button>

            {isExpanded && <HistoryExpandedDetails event={event} locale={locale} className="col-span-full px-m md:px-2xl" onReactivated={onReactivated} />}
        </div>
    );
};
